import React, { useMemo, useState } from 'react';
import { Button, Badge, Space, Typography } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { volcano } from '@ant-design/colors';

const ACTIVE_COLOR = volcano.primary;
const { Text } = Typography;

const LocationListGrouped = ({ locations, selectedLocations, onToggleLocation, searchText }) => {
  // Group locations by country and city/region
  const groupedLocations = useMemo(() => {
    const filtered = searchText 
      ? locations.filter(loc => loc.name.toLowerCase().includes(searchText.toLowerCase()))
      : locations;

    const groups = {};
    
    filtered.forEach(location => {
      const country = location.country || 'Unknown Country';
      const region = location.city_or_region || 'Other';
      
      if (!groups[country]) {
        groups[country] = {};
      }
      if (!groups[country][region]) {
        groups[country][region] = [];
      }
      groups[country][region].push(location);
    });

    return groups;
  }, [locations, searchText]);

  // Count selections in a group
  const countSelected = (locationList) => {
    return locationList.filter(loc => selectedLocations.includes(loc.name)).length;
  };

  // Toggle all locations in a group
  const toggleGroup = (locationList, selectAll) => {
    locationList.forEach(location => {
      const isSelected = selectedLocations.includes(location.name);
      if (selectAll && !isSelected) {
        onToggleLocation(location);
      } else if (!selectAll && isSelected) {
        onToggleLocation(location);
      }
    });
  };

  const countryKeys = Object.keys(groupedLocations).sort();
  const [selectedCountry, setSelectedCountry] = useState(countryKeys[0]);

  if (countryKeys.length === 0) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        color: '#888'
      }}>
        {searchText ? 'No matching locations found' : 'No locations available'}
      </div>
    );
  }

  // Get data for the selected country
  const regions = groupedLocations[selectedCountry] || {};
  const regionKeys = Object.keys(regions).sort();
  const allLocationsInCountry = regionKeys.flatMap(region => regions[region]);
  const countrySelectedCount = countSelected(allLocationsInCountry);
  const countryTotalCount = allLocationsInCountry.length;
  const allCountrySelected = countrySelectedCount === countryTotalCount;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Country Tabs */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ 
          display: 'flex',
          gap: '8px',
          overflowX: 'auto',
          paddingBottom: '8px',
          WebkitOverflowScrolling: 'touch'
        }}>
          {countryKeys.map(country => {
            const countryRegions = groupedLocations[country];
            const countryRegionKeys = Object.keys(countryRegions);
            const countryLocs = countryRegionKeys.flatMap(region => countryRegions[region]);
            const selectedCount = countSelected(countryLocs);
            const isSelected = selectedCountry === country;

            return (
              <Button
                key={country}
                onClick={() => setSelectedCountry(country)}
                style={{ 
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  backgroundColor: isSelected ? ACTIVE_COLOR : '#2a2a2a',
                  color: isSelected ? '#fff' : '#aaa',
                  border: isSelected ? `1px solid ${ACTIVE_COLOR}` : '1px solid #444',
                  transition: 'all 0.3s'
                }}
              >
                <span>{country}</span>
                {selectedCount > 0 && (
                  <Badge 
                    count={selectedCount} 
                    style={{ 
                      backgroundColor: volcano[5],
                      marginLeft: '8px'
                    }} 
                  />
                )}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Country Select All Button */}
      <div style={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
        padding: '8px 12px',
        backgroundColor: '#252525',
        borderRadius: '6px',
        border: '1px solid #333'
      }}>
        <Text style={{ color: '#888', fontSize: '12px' }}>
          {countrySelectedCount} of {countryTotalCount} selected
        </Text>
        <Button
          size="small"
          type={allCountrySelected ? "default" : "primary"}
          icon={allCountrySelected ? <CloseOutlined /> : <CheckOutlined />}
          onClick={() => toggleGroup(allLocationsInCountry, !allCountrySelected)}
          style={{ 
            minWidth: '70px',
            backgroundColor: allCountrySelected ? '#2a2a2a' : ACTIVE_COLOR,
            borderColor: allCountrySelected ? '#444' : ACTIVE_COLOR
          }}
        >
          {allCountrySelected ? 'Clear' : 'Select'} All
        </Button>
      </div>

      {/* Regions and Locations */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '20px',
        alignContent: 'start'
      }}>
        {regionKeys.map(region => {
          const regionLocations = regions[region];
          const regionSelectedCount = countSelected(regionLocations);
          const regionTotalCount = regionLocations.length;
          const allRegionSelected = regionSelectedCount === regionTotalCount;

          return (
            <div key={`${selectedCountry}-${region}`} style={{ minHeight: 'min-content' }}>
              {/* Region Header - More Subtle */}
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '8px',
                paddingBottom: '4px',
                borderBottom: '1px solid #333'
              }}>
                <Text style={{ color: '#999', fontSize: '13px', fontWeight: 400 }}>
                  {region}
                </Text>
                <Space size="small">
                  <Text style={{ color: '#666', fontSize: '11px' }}>
                    {regionSelectedCount}/{regionTotalCount}
                  </Text>
                  <Button
                    size="small"
                    type="text"
                    icon={allRegionSelected ? <CloseOutlined /> : <CheckOutlined />}
                    onClick={() => toggleGroup(regionLocations, !allRegionSelected)}
                    style={{ 
                      fontSize: '11px',
                      height: '22px',
                      minWidth: '60px',
                      color: allRegionSelected ? '#888' : ACTIVE_COLOR,
                      padding: '0 8px'
                    }}
                  >
                    {allRegionSelected ? 'Clear' : 'Select'}
                  </Button>
                </Space>
              </div>

              {/* Location Buttons */}
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                gap: '8px'
              }}>
                {regionLocations.map(location => {
                  const isActive = selectedLocations.includes(location.name);
                  return (
                    <Button
                      key={location.id}
                      onClick={() => onToggleLocation(location)}
                      style={{ 
                        height: 'auto',
                        padding: '10px 8px',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        backgroundColor: isActive ? ACTIVE_COLOR : '#2a2a2a',
                        color: isActive ? '#fff' : '#aaa',
                        border: isActive ? `1px solid ${ACTIVE_COLOR}` : '1px solid #444',
                        transition: 'all 0.3s',
                        fontSize: '12px'
                      }}
                      title={location.name}
                    >
                      {location.name}
                    </Button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LocationListGrouped;
